import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import {
  InventoryItem,
  InventoryMovement,
  VInventoryDashboard,
  VInventoryAlert,
} from './types'

export interface ActionResult {
  success: boolean
  error?: string
}

export interface CreateInventoryItemData {
  restaurant_id: string
  name: string
  unit: string
  cost_per_unit: number
  current_stock: number
  minimum_stock: number
  supplier?: string | null
  notes?: string | null
  is_active?: boolean
}

export interface UpdateInventoryItemData {
  name?: string
  unit?: string
  cost_per_unit?: number
  current_stock?: number
  minimum_stock?: number
  supplier?: string | null
  notes?: string | null
  is_active?: boolean
}

export function useInventoryManagement(restaurantId: string | undefined) {
  const [inventory, setInventory] = useState<VInventoryDashboard[]>([])
  const [alerts, setAlerts] = useState<VInventoryAlert[]>([])
  const [movements, setMovements] = useState<InventoryMovement[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    if (!restaurantId) return
    try {
      setLoading(true)
      const [invRes, alertsRes] = await Promise.all([
        supabase
          .from('v_inventory_dashboard')
          .select('*')
          .eq('restaurant_id', restaurantId),
        supabase
          .from('v_inventory_alerts')
          .select('*')
          .eq('restaurant_id', restaurantId),
      ])

      if (invRes.data) setInventory(invRes.data)
      if (alertsRes.data) setAlerts(alertsRes.data)

      // Fetch recent movements
      if (invRes.data && invRes.data.length > 0) {
        const itemIds = invRes.data.map((i) => i.inventory_item_id)
        const { data: movData } = await supabase
          .from('inventory_movements')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .in('inventory_item_id', itemIds)
          .order('created_at', { ascending: false })
          .limit(20)

        if (movData) setMovements(movData)
      }
    } catch (error) {
      console.error('Error fetching inventory:', error)
    } finally {
      setLoading(false)
    }
  }, [restaurantId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // ─── Create ────────────────────────────────────────────
  const createItem = useCallback(
    async (data: CreateInventoryItemData): Promise<ActionResult> => {
      try {
        const { error } = await supabase.from('inventory_items').insert({
          restaurant_id: restaurantId,
          name: data.name,
          unit: data.unit,
          cost_per_unit: data.cost_per_unit,
          current_stock: data.current_stock,
          minimum_stock: data.minimum_stock,
          supplier: data.supplier || null,
          notes: data.notes || null,
          is_active: data.is_active ?? true,
        })
        if (error) throw error
        await fetchData()
        return { success: true }
      } catch (err: any) {
        console.error('[NOIZA] Error creating inventory item:', err)
        return {
          success: false,
          error: err?.message || 'Error al crear ingrediente',
        }
      }
    },
    [restaurantId, fetchData],
  )

  // ─── Update ────────────────────────────────────────────
  const updateItem = useCallback(
    async (
      itemId: string,
      data: UpdateInventoryItemData,
    ): Promise<ActionResult> => {
      try {
        const updatePayload: any = {
          ...data,
          updated_at: new Date().toISOString(),
        }
        const { error } = await supabase
          .from('inventory_items')
          .update(updatePayload)
          .eq('id', itemId)
        if (error) throw error
        await fetchData()
        return { success: true }
      } catch (err: any) {
        console.error('[NOIZA] Error updating inventory item:', err)
        return {
          success: false,
          error: err?.message || 'Error al actualizar ingrediente',
        }
      }
    },
    [fetchData],
  )

  // ─── Delete ────────────────────────────────────────────
  const deleteItem = useCallback(
    async (itemId: string): Promise<ActionResult> => {
      try {
        const { error } = await supabase
          .from('inventory_items')
          .delete()
          .eq('id', itemId)
        if (error) throw error
        await fetchData()
        return { success: true }
      } catch (err: any) {
        console.error('[NOIZA] Error deleting inventory item:', err)
        return {
          success: false,
          error: err?.message || 'Error al eliminar ingrediente',
        }
      }
    },
    [fetchData],
  )

  // ─── Stock Adjustment ──────────────────────────────────
  const adjustStock = useCallback(
    async (
      itemId: string,
      quantity: number,
      movementType: 'restock' | 'waste' | 'manual_adjustment',
      reason?: string,
    ): Promise<ActionResult> => {
      try {
        // Find current item to compute new stock
        const currentItem = inventory.find(
          (i) => i.inventory_item_id === itemId,
        )
        if (!currentItem) {
          return { success: false, error: 'Ingrediente no encontrado' }
        }

        const delta =
          movementType === 'restock' ? Math.abs(quantity) : -Math.abs(quantity)
        const newStock = Math.max(0, currentItem.current_stock + delta)

        // Update stock
        const { error: updateErr } = await supabase
          .from('inventory_items')
          .update({
            current_stock: newStock,
            updated_at: new Date().toISOString(),
          })
          .eq('id', itemId)
        if (updateErr) throw updateErr

        // Record movement
        const { error: movErr } = await supabase
          .from('inventory_movements')
          .insert({
            restaurant_id: restaurantId,
            inventory_item_id: itemId,
            quantity: delta,
            movement_type: movementType,
            reason: reason || null,
          })
        if (movErr) {
          console.warn('[NOIZA] Movement record failed:', movErr)
        }

        await fetchData()
        return { success: true }
      } catch (err: any) {
        console.error('[NOIZA] Error adjusting stock:', err)
        return {
          success: false,
          error: err?.message || 'Error al ajustar stock',
        }
      }
    },
    [restaurantId, inventory, fetchData],
  )

  // ─── Toggle Active ─────────────────────────────────────
  const toggleItem = useCallback(
    async (itemId: string, isActive: boolean): Promise<ActionResult> => {
      return updateItem(itemId, { is_active: isActive })
    },
    [updateItem],
  )

  return {
    inventory,
    alerts,
    movements,
    loading,
    createItem,
    updateItem,
    deleteItem,
    adjustStock,
    toggleItem,
    refetch: fetchData,
  }
}
